/**
 * MIDI message parsing — pure functions over raw byte arrays.
 *
 * Web MIDI delivers a `Uint8Array` per message. This module decodes that
 * into a typed discriminated union the rest of the app can pattern-match
 * without caring about status-byte bit layout or running-status quirks.
 *
 * Deliberately narrow: only the messages we route right now. The `status`
 * byte's high nibble is the message type, low nibble is the channel (0-15).
 */

export type MidiMessage =
  | { readonly type: 'noteOn'; readonly channel: number; readonly note: number; readonly velocity: number }
  | { readonly type: 'noteOff'; readonly channel: number; readonly note: number; readonly velocity: number }
  | { readonly type: 'cc'; readonly channel: number; readonly controller: number; readonly value: number }
  | { readonly type: 'pitchBend'; readonly channel: number; readonly value: number }
  | { readonly type: 'allNotesOff'; readonly channel: number };

/** MIDI controller numbers we care about by name. */
export const CC = {
  MOD_WHEEL: 1,
  SUSTAIN: 64,
  ALL_NOTES_OFF: 123,
} as const;

/**
 * Decode a single MIDI message. Returns null for:
 *   - shorter-than-expected buffers
 *   - status bytes we don't handle (system messages, clock, SysEx, etc.)
 *
 * Velocity-0 note-on is canonical MIDI for note-off; we normalize it here
 * so callers don't have to re-check.
 */
export function parseMidiMessage(data: Uint8Array | number[]): MidiMessage | null {
  if (!data || data.length < 2) return null;
  const status = data[0];
  if (status === undefined) return null;
  const type = status & 0xf0;
  const channel = status & 0x0f;

  switch (type) {
    case 0x90: {
      // Note on (but velocity 0 is note off)
      if (data.length < 3) return null;
      const note = data[1];
      const velocity = data[2];
      if (note === undefined || velocity === undefined) return null;
      if (velocity === 0) {
        return { type: 'noteOff', channel, note, velocity: 0 };
      }
      return { type: 'noteOn', channel, note, velocity: velocity / 127 };
    }
    case 0x80: {
      // Note off
      if (data.length < 3) return null;
      const note = data[1];
      const velocity = data[2];
      if (note === undefined || velocity === undefined) return null;
      return { type: 'noteOff', channel, note, velocity: velocity / 127 };
    }
    case 0xb0: {
      // Control change
      if (data.length < 3) return null;
      const controller = data[1];
      const value = data[2];
      if (controller === undefined || value === undefined) return null;
      if (controller === CC.ALL_NOTES_OFF) {
        return { type: 'allNotesOff', channel };
      }
      return { type: 'cc', channel, controller, value };
    }
    case 0xe0: {
      // Pitch bend: 14-bit, LSB then MSB, center 8192 → normalize to -1..+1
      if (data.length < 3) return null;
      const lsb = data[1];
      const msb = data[2];
      if (lsb === undefined || msb === undefined) return null;
      const raw = ((msb & 0x7f) << 7) | (lsb & 0x7f);
      const centered = (raw - 8192) / 8192; // -1..+1 (8192/8191 asymmetry, acceptable)
      return { type: 'pitchBend', channel, value: Math.max(-1, Math.min(1, centered)) };
    }
    default:
      return null;
  }
}
